import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Button,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  DirectionsBus as BusIcon,
  CheckCircle as CheckCircleIcon,
  BookOnline as BookIcon,
  DirectionsBus,
  Person as PersonIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getValidToken } from "../utils/authUtils";
import { Loading } from "./Loading";

interface Activity {
  id: string;
  description: string;
  companyId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  creator: {
    id: string;
    firebaseUid: string;
    phoneNumber: string;
    name: string;
    email: string | null;
    role: string;
    companyId: string;
    designation: string | null;
    profilePhoto: string | null;
    status: string;
    lastLoginAt: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
}

interface RecentActivitiesProps {
  activities: Activity[];
}

const getActivities = async (page: number, limit: number) => {
  try {
    const token = await getValidToken();
    const queryParams = new URLSearchParams({
      page: page?.toString() || "1",
      limit: limit?.toString() || "10",
    });

    const url = `${process.env.REACT_APP_API_HOST}recent-activities?${queryParams}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.data?.isSuccess) {
      return response.data.data;
    } else {
      return { data: [], pagination: { total: 0, page: 1, pages: 1 } };
    }
  } catch (error) {
    return { data: [], pagination: { total: 0, page: 1, pages: 1 } };
  }
};

const formatRelativeTime = (dateString: string) => {
  const now = new Date();
  const activityDate = new Date(dateString);
  const diffInSeconds = Math.floor(
    (now.getTime() - activityDate.getTime()) / 1000
  );

  if (diffInSeconds < 60) {
    return `${diffInSeconds} seconds ago`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }
};

const RecentActivities = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);
    getActivities(1, 5) // Initial load with 5 records
      .then((data) => {
        setActivities(data?.data || []);
        setHasMore(data?.pagination?.total > 5);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleShowMore = async () => {
    setIsLoadingMore(true);
    try {
      const data = await getActivities(1, 10); // Load 10 total records (5 + 5 more)
      setActivities(data?.data || []);
      setHasMore(false); // No more loading after this
    } catch (error) {
      console.error("Error loading more activities:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <TableContainer
          sx={{ height: "350px", overflow: "auto" }}
          component={Paper}
          elevation={0}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Recent Activities</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center">
                  <Loading />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "5px",
        boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
        height: "350px",
        overflow: "auto",
        "&::-webkit-scrollbar": {
          width: "3px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f1f1f1",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#c1c1c1",
          borderRadius: "3px",
          "&:hover": {
            backgroundColor: "#a8a8a8",
          },
        },
        "&::-webkit-scrollbar-corner": {
          backgroundColor: "#f1f1f1",
        },
      }}
    >
      <Typography
        sx={{
          position: "sticky",
          top: 0,
          px: 2,
          py: 1,
          borderBottom: "1px solid #e0e0e0",
          bgcolor: "white",
          zIndex: 1,
        }}
        variant="body1"
        gutterBottom
      >
        Recent Activities
      </Typography>
      <TableContainer
        sx={{
          borderWidth: 0,
          elevation: 0,
          borderRadius: 0,
          boxShadow: 0,
        }}
        component={Paper}
        elevation={0}
      >
        <Table>
          <TableBody>
            {activities.length === 0 ? (
              <TableRow>
                <TableCell align="center">
                  <Typography variant="body2" color="textSecondary">
                    No activities found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              activities.map((activity) => (
                <TableRow key={activity.id} hover>
                  <TableCell sx={{ border: "none", py: 0 }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                    >
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            fontSize: "0.5rem",
                            bgcolor: "#1976d2",
                            flexShrink: 0,
                          }}
                        >
                          {activity?.creator?.name
                            ? activity?.creator?.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .toUpperCase()
                            : ""}
                        </Avatar>
                        <Tooltip title={activity.description} placement="top">
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              component="div"
                              color="text.primary"
                              fontSize="0.7rem"
                              sx={{
                                wordBreak: "break-word",
                                lineHeight: 1.4,
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: 500,
                                  fontSize: "0.7rem",
                                  color: "grey.500",
                                }}
                              >
                                {activity?.creator?.name || "Admin"}:
                              </span>{" "}
                              {activity.description}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          ml: 0.5,
                          fontSize: "0.6rem",
                          fontWeight: 400,
                          fontStyle: "italic",
                        }}
                        width="100%"
                        textAlign="right"
                      >
                        {formatRelativeTime(activity.createdAt)}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {hasMore && (
          <Box
            sx={{
              display: "flex",
            }}
          >
            <Button
              variant="text"
              size="small"
              onClick={handleShowMore}
              disabled={isLoadingMore}
              sx={{
                textTransform: "none",
                fontSize: "0.7rem",
                fontWeight: 400,
                fontStyle: "italic",
                textDecoration: isLoadingMore ? "none" : "underline",
                color: "primary.main",
                pt: 0,
                pb: 1,
                "&:hover": {
                  textDecoration: isLoadingMore ? "none" : "underline",
                },
              }}
            >
              {isLoadingMore ? "Loading..." : "Show More"}
            </Button>
          </Box>
        )}
      </TableContainer>
    </Box>
  );
};

export default RecentActivities;
